import {
  firebaseAPIKey,
  firebaseAPPID,
  firebaseDatabaseURL,
  firebaseProjectID
} from "config/constant";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import { getDatabase, onValue, ref, get } from "firebase/database";
import {
  setFireBaseConnectionState,
  setFireBaseState,
  setUiErrorScreen,
  setSessionStatus
} from "store/app.slice";
import * as Sentry from "@sentry/react";
import { initializeApp } from "firebase/app";
import { getErrorType } from "../../helpers/precheck-helper";
import { ProctoringRoute, getEndpoint } from "../../routes/route";
import { NavigateFunction } from "react-router-dom";
import { SecondaryDeviceStatus, SessionStatus, firebaseCollections } from "globals/enums";
import firebaseCustomTokenApi from "../../services/firebase-custom-token";

type Props = {
  navigate: NavigateFunction;
};

const firebaseConfig = {
  apiKey: firebaseAPIKey,
  databaseURL: firebaseDatabaseURL,
  projectId: firebaseProjectID,
  appId: firebaseAPPID
};

const app = initializeApp(firebaseConfig);
const firebaseAuthentication = getAuth(app);
const firebaseDatabase = getDatabase(app);

const getSecondaryDeviceStatus = (secondaryDeviceObject: any) => {
  if (!secondaryDeviceObject) return undefined;
  const values = Object.values(secondaryDeviceObject);
  const hasConnected = values.some((item: any) => item.status === SecondaryDeviceStatus.Connected);
  const hasDisconnected = values.some(
    (item: any) => item.status === SecondaryDeviceStatus.Disconnected
  );
  const allInactive = values.every((item: any) => item.status === SecondaryDeviceStatus.InActive);
  if (hasConnected) return SecondaryDeviceStatus.Connected;
  if (hasDisconnected) return SecondaryDeviceStatus.Disconnected;
  if (allInactive) return SecondaryDeviceStatus.InActive;
  return "unknown status";
};

const createSecondaryStatusChecker = (sessionRef: any, dispatch: any) => {
  let timer30: ReturnType<typeof setTimeout> | null = null;
  let timer60: ReturnType<typeof setTimeout> | null = null;

  const clear = () => {
    if (timer30) {
      clearTimeout(timer30);
      timer30 = null;
    }
    if (timer60) {
      clearTimeout(timer60);
      timer60 = null;
    }
  };

  const start = (cb: (status: string) => void) => {
    timer30 = setTimeout(() => {
      get(sessionRef).then((snapshot) => {
        const newStatus = getSecondaryDeviceStatus(snapshot.val()?.streaming_device?.secondary);
        if (newStatus === SecondaryDeviceStatus.Connected) {
          clear();
          dispatch(setFireBaseState({ secondary_device_status: newStatus }));
        } else {
          timer60 = setTimeout(() => {
            get(sessionRef).then((snap) => {
              const newStatus2 = getSecondaryDeviceStatus(snap.val()?.streaming_device?.secondary);
              if (newStatus2 !== SecondaryDeviceStatus.Connected) {
                cb(newStatus2);
              }
              clear();
            });
          }, 30000);
        }
      });
    }, 30000);
  };

  return { start, clear };
};

let currentSessionUnsubscribe: (() => void) | null = null;
let currentSecondaryChecker: ReturnType<typeof createSecondaryStatusChecker> | null = null;

const FirebaseCleanup = () => {
  if (currentSessionUnsubscribe) {
    currentSessionUnsubscribe();
    currentSessionUnsubscribe = null;
  }
  if (currentSecondaryChecker) {
    currentSecondaryChecker.clear();
    currentSecondaryChecker = null;
  }
};

const FirebaseInit = async (id: number, dispatch: any, { navigate }: Props) => {
  FirebaseCleanup();

  try {
    const { data, isSuccess } = await dispatch(
      firebaseCustomTokenApi.endpoints.firebaseCustomToken.initiate({})
    );
    if (!isSuccess) return;

    signInWithCustomToken(firebaseAuthentication, data)
      .then(() => {
        const sessionRef = ref(firebaseDatabase, `${firebaseCollections.Session}/${id}`);
        const secondaryChecker = createSecondaryStatusChecker(sessionRef, dispatch);
        currentSecondaryChecker = secondaryChecker;
        let prevSecondaryStatus: string | undefined;
        let isDelayActive = false;

        const handleSessionData = (snapshot: any) => {
          const data = snapshot.val();
          if (!data) {
            dispatch(setFireBaseConnectionState({ is_connected: false }));
            return;
          }
          const commonState = {
            pre_check: data.pre_check,
            status: data.status,
            termination_reason: data.termination_reason,
            proctor_group_room_identity: data.proctor_group_room_identity,
            audio_call_initiated: data.audio_call_initiated,
            is_secondary_disabled: data?.streaming_device?.is_secondary_disabled || false
          };
          dispatch(setFireBaseState(commonState));

          const currentSecondaryStatus = getSecondaryDeviceStatus(
            data?.streaming_device?.secondary
          );

          if (currentSecondaryStatus === SecondaryDeviceStatus.Connected) {
            if (isDelayActive) {
              isDelayActive = false;
              secondaryChecker.clear();
            }
            dispatch(setFireBaseState({ secondary_device_status: currentSecondaryStatus }));
          } else {
            if (prevSecondaryStatus === SecondaryDeviceStatus.Connected) {
              if (!isDelayActive) {
                isDelayActive = true;
                secondaryChecker.start((newStatus) => {
                  dispatch(setFireBaseState({ secondary_device_status: newStatus }));
                  isDelayActive = false;
                });
              }
            } else if (!isDelayActive) {
              dispatch(setFireBaseState({ secondary_device_status: currentSecondaryStatus }));
            }
          }
          prevSecondaryStatus = currentSecondaryStatus;

          const sessionError = getErrorType(data?.status);
          if (sessionError) {
            dispatch(setUiErrorScreen({ uiErrorScreen: sessionError as string }));
            if (data?.status === SessionStatus.Terminated) {
              const event = new CustomEvent("Terminated");
              document.dispatchEvent(event);
              dispatch(setSessionStatus({ status: SessionStatus.Terminated }));
              navigate(getEndpoint(ProctoringRoute.Terminated));
            } else if (data?.status === SessionStatus.Completed) {
              const event = new CustomEvent("StopProctorClient");
              document.dispatchEvent(event);
              dispatch(setSessionStatus({ status: SessionStatus.Completed }));
            }
          }
          dispatch(setFireBaseConnectionState({ is_connected: true }));
        };

        currentSessionUnsubscribe = onValue(sessionRef, handleSessionData);
      })
      .catch((error) => {
        Sentry.captureException(error);
      });
  } catch (e) {
    dispatch(setFireBaseConnectionState({ is_connected: false }));
    throw e;
  }
};

export { FirebaseInit, FirebaseCleanup, createSecondaryStatusChecker };
